import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import api from "../../services/api";
import { endpoints } from "../../services/endpoints";
import { fmtDate } from "../../utils/format";
import { iconGreen, iconBlue } from "../../utils/mapIcons";
import { AutoFitBounds } from "../../utils/map";

// Array vacío estable
const EMPTY = [];

export default function DashboardPrincipalAdmin() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        const res = await api.get(endpoints.padlock.summary); // /padlock/summary
        const payload = res.data?.data ?? res.data;
        if (alive) setSummary(payload);
      } catch (e) {
        if (alive) setErr(e?.message || "Error cargando datos");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const total        = summary?.total ?? 0;
  const totalActive  = summary?.active ?? 0;
  const totalInMotion = summary?.inMotion ?? 0;

  // Lista base estable desde el backend
  const padlocks = useMemo(
    () => (Array.isArray(summary?.latestLocations) ? summary.latestLocations : EMPTY),
    [summary?.latestLocations]
  );

  // Markers solo con coordenadas válidas
  const markers = useMemo(
    () =>
      padlocks
        .filter(p => p && p.lat != null && p.lng != null)
        .map(p => ({
          id: p.id,
          name: p.name,
          serialNumber: p.serialNumber,
          status: (p.status ?? "UNKNOWN").toUpperCase(),
          lat: Number(p.lat),
          lng: Number(p.lng),
          updated: p.updated ?? p.eventAt ?? null,
        })),
    [padlocks]
  );

  const mapCenter = useMemo(
    () => (markers.length ? [markers[0].lat, markers[0].lng] : [40.4168, -3.7038]),
    [markers]
  );

  return (
    <div className="space-y-6 p-4">
      <h1 className="text-2xl font-semibold">Panel de control</h1>
      <p className="text-gray-600">Resumen de candados y localizaciones</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-gray-500">Dispositivos activos</p>
          <h2 className="text-3xl font-bold text-green-600">{totalActive}</h2>
        </div>

        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-gray-500">En movimiento</p>
          <h2 className="text-3xl font-bold text-blue-600">{totalInMotion}</h2>
          <p className="text-xs text-gray-400 mt-1">Se añadirá más adelante</p>
        </div>

        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-gray-500">Última actualización</p>
          <h2 className="text-lg font-semibold">
            {fmtDate(summary?.generatedAt || new Date())}
          </h2>
        </div>

        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-gray-500">Total dispositivos</p>
          <h2 className="text-3xl font-bold text-gray-800">{total}</h2>
        </div>
      </div>

      {loading && <div className="bg-white rounded-xl shadow p-4">Cargando…</div>}
      {err && <div className="bg-white rounded-xl shadow p-4 text-red-600">Error: {err}</div>}

      <div className="bg-white rounded-xl shadow p-4 h-[500px]">
        <h2 className="text-lg font-semibold mb-4">Últimas localizaciones</h2>
        <MapContainer center={mapCenter} zoom={6} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />

          {markers.length > 0 && (
            <AutoFitBounds points={markers.map(({ lat, lng }) => ({ lat, lng }))} />
          )}

          {markers.map((p) => {
            const moving = p.status === "MOVING";
            return (
              <Marker key={p.id} position={[p.lat, p.lng]} icon={moving ? iconGreen : iconBlue}>
                <Popup>
                  <strong>{p.name}</strong> <br />
                  Nº serie: {p.serialNumber} <br />
                  Estado: {p.status === "ACTIVE" ? "✅ Activo" : p.status === "MOVING" ? "🚚 En movimiento" : "⛔ Inactivo"} <br />
                  Última señal: {fmtDate(p.updated)}
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>

        {padlocks.some((p) => p?.lat == null || p?.lng == null) && (
          <p className="mt-3 text-sm text-gray-500">
            Hay candados sin posición reciente y no aparecen en el mapa. Siguen contando en las métricas.
          </p>
        )}
      </div>
    </div>
  );
}